import { authService } from './auth.service.js';

// Industry standard auth session configuration
const AUTH_SESSION_CONFIG = {
  idleTimeout: 30 * 60 * 1000, // 30 minutes of inactivity
  maxSessionDuration: 8 * 60 * 60 * 1000, // 8 hours total
  rememberMeDuration: 7 * 24 * 60 * 60 * 1000, // 7 days
  checkInterval: 60 * 1000, // Check every minute
};

export interface AuthSessionData {
  loginTime: number;
  lastActivity: number;
  rememberMe: boolean;
  sessionId: string;
}

export class AuthSessionService {
  private sessionData: AuthSessionData | null = null;
  private idleTimer: NodeJS.Timeout | null = null;
  private maxSessionTimer: NodeJS.Timeout | null = null;
  private checkInterval: NodeJS.Timeout | null = null;
  private logoutListeners: (() => void)[] = [];

  constructor() {
    this.loadSessionFromStorage();
    this.setupActivityListeners();
    this.startSessionCheck();
  }

  // Initialize session when user logs in
  startSession(rememberMe: boolean = false): void {
    const sessionId = this.generateSessionId();
    this.sessionData = {
      loginTime: Date.now(),
      lastActivity: Date.now(),
      rememberMe,
      sessionId,
    };

    this.saveSessionToStorage();
    this.startTimers();
    
  }

  // Update last activity time
  updateActivity(): void {
    if (!this.sessionData) return;

    this.sessionData.lastActivity = Date.now();
    this.saveSessionToStorage();
    this.resetIdleTimer();
  }

  // End current session
  endSession(): void {
    this.clearAllTimers();
    this.sessionData = null;
    this.clearSessionFromStorage();
    
  }

  // Check if session is valid
  isSessionValid(): boolean {
    if (!this.sessionData) return false;

    const now = Date.now();
    const timeSinceLogin = now - this.sessionData.loginTime;
    const timeSinceActivity = now - this.sessionData.lastActivity;

    // Check max session duration
    if (timeSinceLogin > AUTH_SESSION_CONFIG.maxSessionDuration) {
      
      return false;
    }

    // Check idle timeout
    if (timeSinceActivity > AUTH_SESSION_CONFIG.idleTimeout) {
      
      return false;
    }

    return true;
  }

  // Get remaining session time
  getRemainingTime(): { idle: number; max: number } {
    if (!this.sessionData) return { idle: 0, max: 0 };

    const now = Date.now();
    const timeSinceLogin = now - this.sessionData.loginTime;
    const timeSinceActivity = now - this.sessionData.lastActivity;

    return {
      idle: Math.max(0, AUTH_SESSION_CONFIG.idleTimeout - timeSinceActivity),
      max: Math.max(0, AUTH_SESSION_CONFIG.maxSessionDuration - timeSinceLogin),
    };
  }


  // Add logout listener
  onLogout(callback: () => void): () => void {
    this.logoutListeners.push(callback);
    return () => {
      const index = this.logoutListeners.indexOf(callback);
      if (index > -1) this.logoutListeners.splice(index, 1);
    };
  }

  // Private methods
  private generateSessionId(): string {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  private setupActivityListeners(): void {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, () => this.updateActivity(), true);
    });
  }

  private startSessionCheck(): void {
    this.checkInterval = setInterval(() => {
      if (this.sessionData && !this.isSessionValid()) {
        this.handleSessionExpired();
      }
    }, AUTH_SESSION_CONFIG.checkInterval);
  }

  private startTimers(): void {
    this.resetIdleTimer();
    this.startMaxSessionTimer();
  }

  private resetIdleTimer(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);

    // Set idle timeout
    this.idleTimer = setTimeout(() => {
      this.handleSessionExpired();
    }, AUTH_SESSION_CONFIG.idleTimeout);
  }

  private startMaxSessionTimer(): void {
    if (this.maxSessionTimer) clearTimeout(this.maxSessionTimer);

    this.maxSessionTimer = setTimeout(() => {
      this.handleSessionExpired();
    }, AUTH_SESSION_CONFIG.maxSessionDuration);
  }


  private handleSessionExpired(): void {
    
    this.logoutListeners.forEach(callback => callback());
    this.endSession();
  }

  private clearAllTimers(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    if (this.maxSessionTimer) clearTimeout(this.maxSessionTimer);
    if (this.checkInterval) clearInterval(this.checkInterval);
  }

  private saveSessionToStorage(): void {
    if (!this.sessionData) return;

    const storageKey = this.sessionData.rememberMe ? 'cmms_session_persistent' : 'cmms_session_temp';
    localStorage.setItem(storageKey, JSON.stringify(this.sessionData));
  }

  private loadSessionFromStorage(): void {
    // Try persistent storage first
    const persistentData = localStorage.getItem('cmms_session_persistent');
    if (persistentData) {
      try {
        this.sessionData = JSON.parse(persistentData);
        if (this.isSessionValid()) {
          
          this.startTimers();
          return;
        }
      } catch (error) {
        console.warn('Failed to parse persistent session data');
      }
    }

    // Try temporary storage
    const tempData = localStorage.getItem('cmms_session_temp');
    if (tempData) {
      try {
        this.sessionData = JSON.parse(tempData);
        if (this.isSessionValid()) {
          
          this.startTimers();
          return;
        }
      } catch (error) {
        console.warn('Failed to parse temporary session data');
      }
    }

    // No valid session found
    this.sessionData = null;
  }

  private clearSessionFromStorage(): void {
    localStorage.removeItem('cmms_session_persistent');
    localStorage.removeItem('cmms_session_temp');
  }
}

// Create and export singleton instance
export const authSessionService = new AuthSessionService();
